import { useState, useEffect, useCallback } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import {
  ArrowLeft, Trophy, Users, CalendarBlank, MapPin, GameController,
  ChartBar, ListBullets, TreeStructure, Star, Lightning, Clock, Table as TableIcon, PencilSimple, FilePdf, UsersThree, Share
} from "@phosphor-icons/react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import TournamentWizard from "@/components/TournamentWizard";
import ProgressionDialog from "@/components/tournament/ProgressionDialog";
import GroupEditor from "@/components/GroupEditor";
import RescheduleMatchDialog from "@/components/tournament/RescheduleMatchDialog";
import ShareMatchDialog from "@/components/match/ShareMatchDialog";
import GroupTable from "@/components/tournament/GroupTable";
import BracketView from "@/components/tournament/BracketView";
import ProbableBracketCard from "@/components/tournament/ProbableBracketCard";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function TournamentDetail() {
  const { tournamentId, tab: tabFromUrl } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [tournament, setTournament] = useState(null);
  const [matches, setMatches] = useState([]);
  const [players, setPlayers] = useState([]);
  const [groups, setGroups] = useState([]);
  const [stats, setStats] = useState(null);
  const [standings, setStandings] = useState([]);
  const [highlights, setHighlights] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showEditWizard, setShowEditWizard] = useState(false);
  const [showProgression, setShowProgression] = useState(false);
  const [showGroupEditor, setShowGroupEditor] = useState(false);
  const [reschedulingMatch, setReschedulingMatch] = useState(null);
  const [sharingMatch, setSharingMatch] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      // Step 1: Resolve the slug-or-UUID URL param to the canonical tournament.
      // Once we have `tournament.id` (UUID), all sibling endpoints are called
      // with that UUID — keeps the rest of the API stable.
      const tRes = await axios.get(`${API}/tournaments/${tournamentId}`);
      setTournament(tRes.data);
      const canonicalId = tRes.data?.id || tournamentId;

      // If user arrived via UUID but tournament has a prettier slug, rewrite
      // the URL to the slug for shareable links + cleaner browser history.
      // Skip if already on the slug, or if no slug exists yet.
      if (
        tRes.data?.slug
        && tournamentId !== tRes.data.slug
        && tournamentId === canonicalId
      ) {
        const tabSuffix = tabFromUrl ? `/${tabFromUrl}` : "";
        navigate(`/tournaments/${tRes.data.slug}${tabSuffix}`, { replace: true });
      }

      const [mRes, pRes] = await Promise.all([
        axios.get(`${API}/tournaments/${canonicalId}/matches`),
        axios.get(`${API}/players`),
      ]);
      setMatches(mRes.data);
      setPlayers(pRes.data);

      // Fetch optional data in parallel
      const [groupsRes, statsRes, standingsRes, highlightsRes] = await Promise.all([
        axios.get(`${API}/tournaments/${canonicalId}/groups`).catch(() => ({ data: [] })),
        axios.get(`${API}/tournaments/${canonicalId}/stats`).catch(() => ({ data: null })),
        axios.get(`${API}/tournaments/${canonicalId}/league-standings`).catch(() => ({ data: [] })),
        axios.get(`${API}/tournaments/${canonicalId}/highlights`).catch(() => ({ data: [] })),
      ]);
      setGroups(groupsRes.data);
      setStats(statsRes.data);
      setStandings(standingsRes.data);
      setHighlights(highlightsRes.data);
    } catch (e) {
      console.error("Error fetching tournament:", e);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tournamentId]);

  useEffect(() => { fetchData(); }, [fetchData]);

  /**
   * Streams the server-rendered PDF directly to the user's downloads.
   *
   * Previously the Export Report button did `window.open(...pdf-endpoint, '_blank')`
   * which opened the report in a new tab and forced the admin to use the
   * browser's "Save as PDF" flow. Now we fetch the bytes ourselves and
   * trigger a download, so the click → file save UX is one step.
   */
  const downloadTournamentReport = async (t) => {
    if (!t?.id) return;
    const safeName = (t.name || "tournament").replace(/[^a-zA-Z0-9_-]/g, "_").slice(0, 60);
    const downloadingToast = toast.loading("Preparing report PDF…");
    try {
      const res = await axios.get(`${API}/tournaments/${t.id}/export/pdf`, {
        responseType: "blob",
      });
      const blob = new Blob([res.data], {
        type: res.headers["content-type"] || "application/pdf",
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${safeName}_report.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Report downloaded", { id: downloadingToast });
    } catch (err) {
      toast.error(
        err.response?.data?.detail || "Failed to generate report",
        { id: downloadingToast },
      );
    }
  };

  // Auto-navigate admin to bracket tab when tournament hits knockout_pending
  // (all groups done + no KO matches yet). The bracket tab now hosts the
  // unified "Generate Knockout from Groups" flow inside ProbableBracketCard.
  useEffect(() => {
    if (!user?.is_admin || !tournament) return;
    if (tournament.computed_status !== "knockout_pending") return;
    if (tabFromUrl === "bracket") return;  // already there
    navigate(`/tournaments/${tournamentId}/bracket`);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.is_admin, tournament?.id, tournament?.computed_status]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!tournament) {
    return (
      <div className="text-center py-12">
        <p className="text-muted-foreground">Tournament not found</p>
        <Link to="/tournaments"><Button variant="outline" className="mt-4"><ArrowLeft className="w-4 h-4 mr-2" />Back</Button></Link>
      </div>
    );
  }

  const playerMap = {};
  players.forEach(p => { playerMap[p.id] = p; });
  const registeredPlayers = (tournament.player_ids || []).map(pid => playerMap[pid]).filter(Boolean);
  const isGroupFormat = tournament.tournament_format === "groups_knockout" || tournament.tournament_format === "league";
  const statusColor = tournament.status === "ongoing" || tournament.status === "in_progress" ? "text-primary border-primary/30" : "text-chart-3 border-chart-3/30";
  const hasCompletedMatches = matches.some(m => ["completed", "walkover", "live"].includes(m.status));
  const canEdit = user?.is_admin && !hasCompletedMatches;

  // ---- Phase 3: state-driven UI ----
  // `computed_status` comes from the backend (tournament_status.py) and
  // captures the lifecycle: upcoming → group_stage → knockout_pending →
  // knockout_live → completed. Tabs + admin actions key off it.
  const computedStatus = tournament.computed_status || "upcoming";
  const isGroupsKnockout = tournament.tournament_format === "groups_knockout";
  // Bracket tab visible for any groups_knockout tournament from group_stage
  // onwards (so admins can preview the probable bracket + force-lock early
  // if needed). Also visible for any non-groups_knockout tournament.
  const showBracketTab =
    !isGroupsKnockout ||
    ["group_stage", "knockout_pending", "knockout_live", "completed"].includes(computedStatus);
  // Groups tab visible only when format actually has groups
  const showGroupsTab = isGroupFormat || groups.length > 0;
  // Generate-bracket admin button is meaningful any time from group_stage
  // onwards (admin can lock early with force-confirm). Hidden in upcoming
  // (no matches yet) and after knockout has been generated.
  const showGenerateBracketBtn =
    user?.is_admin && isGroupsKnockout &&
    ["group_stage", "knockout_pending"].includes(computedStatus);

  // URL-driven tab selection (Phase 3 refactor 2026-04-29)
  const VALID_TABS = ["schedule", "groups", "bracket", "players", "stats"];
  let activeTab = VALID_TABS.includes(tabFromUrl) ? tabFromUrl : "schedule";
  // Auto-correct invalid combos: if URL says groups but format is pure
  // knockout, fall back to schedule. Same for bracket if hidden.
  if (activeTab === "groups" && !showGroupsTab) activeTab = "schedule";
  if (activeTab === "bracket" && !showBracketTab) activeTab = "schedule";
  const handleTabChange = (next) => {
    if (next === "schedule") navigate(`/tournaments/${tournamentId}`);
    else navigate(`/tournaments/${tournamentId}/${next}`);
  };

  if (showEditWizard && canEdit) {
    return (
      <TournamentWizard
        editTournament={tournament}
        onComplete={(tid) => { setShowEditWizard(false); fetchData(); }}
        onCancel={() => setShowEditWizard(false)}
      />
    );
  }

  // Group matches by date for schedule
  const scheduledMatches = [...matches].sort((a, b) => {
    const dA = a.scheduled_date || "", dB = b.scheduled_date || "";
    if (dA !== dB) return dA.localeCompare(dB);
    return (a.scheduled_time || "").localeCompare(b.scheduled_time || "");
  });
  const matchesByDate = {};
  scheduledMatches.forEach(m => {
    const d = m.scheduled_date || "Unscheduled";
    if (!matchesByDate[d]) matchesByDate[d] = [];
    matchesByDate[d].push(m);
  });

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-6">
      <Link to="/tournaments">
        <Button variant="ghost" className="text-muted-foreground hover:text-foreground" data-testid="back-to-tournaments">
          <ArrowLeft className="w-4 h-4 mr-2" /> Tournaments
        </Button>
      </Link>

      {/* Tournament Header */}
      <Card className="bg-card border-border">
        <CardContent className="p-6">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div>
              <div className="flex items-center gap-3 mb-2">
                <Trophy className="w-6 h-6 text-secondary" weight="fill" />
                <h1 className="font-heading text-2xl font-bold" data-testid="tournament-name">{tournament.name}</h1>
                <Badge variant="outline" className={statusColor}>{tournament.status}</Badge>
              </div>
              <div className="flex flex-wrap gap-4 text-sm text-muted-foreground">
                {tournament.date && <span className="flex items-center gap-1"><CalendarBlank className="w-4 h-4" />{tournament.date}</span>}
                <span className="flex items-center gap-1"><Users className="w-4 h-4" />{registeredPlayers.length} players</span>
                <span className="capitalize">{tournament.tournament_format?.replace('_', ' ') || tournament.type}</span>
                <span>Best of {tournament.best_of || 5}</span>
              </div>
            </div>
            {stats && stats.total_matches > 0 && (
              <div className="flex gap-4 text-center">
                <div>
                  <p className="font-heading font-bold text-lg text-primary">{stats.completed_matches}/{stats.total_matches}</p>
                  <p className="text-[10px] text-muted-foreground">Matches</p>
                </div>
                <div>
                  <p className="font-heading font-bold text-lg text-secondary">{stats.highest_break}</p>
                  <p className="text-[10px] text-muted-foreground">High Break</p>
                </div>
                <div>
                  <p className="font-heading font-bold text-lg">{stats.total_frames}</p>
                  <p className="text-[10px] text-muted-foreground">Frames</p>
                </div>
              </div>
            )}
            {canEdit && (
              <Button variant="outline" size="sm" className="border-secondary/40 text-secondary"
                onClick={() => setShowEditWizard(true)} data-testid="edit-tournament-btn">
                <PencilSimple className="w-4 h-4 mr-1" />Edit Tournament
              </Button>
            )}
            {showGenerateBracketBtn && (
              <Button variant="outline" size="sm" className="border-primary/40 text-primary"
                onClick={() => navigate(`/tournaments/${tournamentId}/bracket`)} data-testid="open-progression-btn">
                <TreeStructure className="w-4 h-4 mr-1" />Generate Bracket
              </Button>
            )}
            <Button
              variant="outline" size="sm" className="border-primary/40 text-primary"
              onClick={() => downloadTournamentReport(tournament)}
              data-testid="export-tournament-report-btn"
            >
              <FilePdf className="w-4 h-4 mr-1" />Export Report
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Tabs — URL-driven (Phase 3 refactor 2026-04-29):
          /tournaments/:id           → schedule (default)
          /tournaments/:id/groups    → groups & tables (groups_knockout only)
          /tournaments/:id/bracket   → bracket / probable bracket
          /tournaments/:id/players   → players list
          /tournaments/:id/stats     → tournament stats

          Visibility is gated on `computedStatus`:
            • Groups tab: groups_knockout format
            • Bracket tab: not visible while pure-upcoming for groups_knockout
              (no bracket nor probable bracket yet)
       */}
      <Tabs value={activeTab} onValueChange={handleTabChange} className="space-y-4">
        <TabsList className="bg-muted/50 flex-wrap">
          <TabsTrigger value="schedule" data-testid="tab-schedule">
            <CalendarBlank className="w-4 h-4 mr-1" />Schedule
          </TabsTrigger>
          {showGroupsTab && (
            <TabsTrigger value="groups" data-testid="tab-groups">
              <ListBullets className="w-4 h-4 mr-1" />Groups & Tables
            </TabsTrigger>
          )}
          {showBracketTab && (
            <TabsTrigger value="bracket" data-testid="tab-bracket">
              <TreeStructure className="w-4 h-4 mr-1" />Bracket
            </TabsTrigger>
          )}
          <TabsTrigger value="players" data-testid="tab-players">
            <Users className="w-4 h-4 mr-1" />Players
          </TabsTrigger>
          <TabsTrigger value="stats" data-testid="tab-stats">
            <ChartBar className="w-4 h-4 mr-1" />Stats
          </TabsTrigger>
        </TabsList>

        {/* Schedule Tab */}
        <TabsContent value="schedule">
          <Card>
            {user?.is_admin && tournament.status !== "completed" && (
              <CardHeader className="pb-2 flex flex-row items-center justify-between">
                <CardTitle className="text-sm text-muted-foreground">Schedule Management</CardTitle>
                <Button
                  variant="outline" size="sm" className="border-border"
                  data-testid="auto-schedule-btn"
                  onClick={async () => {
                    try {
                      const res = await axios.post(`${API}/tournaments/${tournament.id}/auto-schedule`, {}, { withCredentials: true });
                      toast.success(`Scheduled ${res.data.scheduled || 0} matches`);
                      fetchData();
                    } catch (err) {
                      toast.error(err.response?.data?.detail || "Auto-schedule failed");
                    }
                  }}
                >
                  <Clock className="w-4 h-4 mr-1" />Auto Schedule
                </Button>
              </CardHeader>
            )}
            <CardContent className="p-4 space-y-4">
              {Object.keys(matchesByDate).length > 0 ? Object.entries(matchesByDate).map(([date, dateMatches]) => (
                <div key={date}>
                  <h3 className="font-heading font-semibold text-sm text-muted-foreground mb-2 uppercase tracking-wider">
                    {date === "Unscheduled" ? "Unscheduled" : new Date(date + 'T00:00').toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
                    <span className="text-[10px] font-normal ml-2">({dateMatches.length} {dateMatches.length === 1 ? 'match' : 'matches'})</span>
                  </h3>
                  <div className="space-y-2">
                    {dateMatches.map(m => {
                      return (
                        <div key={m.id} className={`flex items-center gap-2 sm:gap-3 p-2.5 sm:p-3 rounded-lg border ${
                          m.status === "live" ? "border-primary/40 bg-primary/5" :
                          m.status === "completed" || m.status === "walkover" ? "border-chart-3/20 bg-chart-3/5" : "border-border"
                        }`} data-testid={`schedule-match-${m.id}`}>
                          <div className="flex flex-col items-center w-14 sm:w-16 shrink-0">
                            <span className="text-xs sm:text-sm font-bold">{m.scheduled_time || "-"}</span>
                            {m.table_name && <span className="text-[9px] sm:text-[10px] text-muted-foreground">{m.table_name}</span>}
                          </div>
                          <div className="w-px h-8 bg-border shrink-0" />
                          <div className="flex-1 flex items-center justify-between min-w-0">
                            <span className={`text-xs sm:text-sm font-medium truncate flex-1 text-right ${m.winner_id === m.player1_id ? "text-primary font-bold" : ""}`}>
                              {playerMap[m.player1_id]?.name || m.player1_name || "TBD"}
                            </span>
                            <div className="px-2 sm:px-3 shrink-0">
                              {m.status === "completed" || m.status === "walkover" || m.status === "live" ? (
                                <span className="font-heading font-bold text-sm">{m.player1_score || 0} - {m.player2_score || 0}</span>
                              ) : (
                                <span className="text-xs text-muted-foreground">vs</span>
                              )}
                            </div>
                            <span className={`text-xs sm:text-sm font-medium truncate flex-1 ${m.winner_id === m.player2_id ? "text-primary font-bold" : ""}`}>
                              {playerMap[m.player2_id]?.name || m.player2_name || "TBD"}
                            </span>
                          </div>
                          <div className="flex flex-col items-end gap-0.5 shrink-0">
                            {m.round && <Badge variant="outline" className="text-[9px] sm:text-[10px] capitalize">{m.round.replace(/_/g, ' ')}</Badge>}
                            <Badge variant="outline" className={`text-[9px] sm:text-[10px] ${
                              m.status === "live" ? "text-primary border-primary/30" :
                              m.status === "completed" ? "text-chart-3 border-chart-3/30" :
                              m.status === "walkover" ? "text-secondary border-secondary/30" : ""
                            }`}>
                              {m.status === "live" && <span className="w-1.5 h-1.5 bg-primary rounded-full mr-1 animate-pulse" />}
                              {m.status}
                            </Badge>
                            {user?.is_admin && !["completed", "walkover", "live"].includes(m.status) && (
                              <Button
                                size="sm" variant="ghost"
                                className="h-6 px-2 text-[10px] text-primary hover:text-primary"
                                onClick={() => setReschedulingMatch({
                                  ...m,
                                  player1_name: playerMap[m.player1_id]?.name || m.player1_name,
                                  player2_name: playerMap[m.player2_id]?.name || m.player2_name,
                                })}
                                data-testid={`reschedule-btn-${m.id}`}
                              >
                                <Clock className="w-3 h-3 mr-1" />Reschedule
                              </Button>
                            )}
                            {["completed", "walkover"].includes(m.status) && (
                              <Button
                                size="sm" variant="ghost"
                                className="h-6 px-2 text-[10px] text-secondary hover:text-secondary"
                                onClick={() => setSharingMatch({
                                  ...m,
                                  player1_name: playerMap[m.player1_id]?.name || m.player1_name,
                                  player2_name: playerMap[m.player2_id]?.name || m.player2_name,
                                  tournament_name: tournament.name,
                                })}
                                data-testid={`share-btn-${m.id}`}
                              >
                                <Share className="w-3 h-3 mr-1" weight="fill" />Share
                              </Button>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )) : (
                <div className="text-center py-8 text-muted-foreground">
                  <CalendarBlank className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p>No matches scheduled yet</p>
                  {user?.is_admin && matches.length > 0 && (
                    <p className="text-xs mt-2">Click "Auto Schedule" above to assign dates, times and tables</p>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Groups & Points Tables */}
        {(isGroupFormat || groups.length > 0) && (
          <TabsContent value="groups">
            <Card>
              <CardContent className="p-4">
                {groups.length > 0 ? groups.map(g => (
                  <GroupTable key={g.name || g.id} group={g} matches={matches} players={players} />
                )) : standings.length > 0 ? (
                  <div>
                    <h3 className="font-heading font-semibold mb-3 text-primary">League Standings</h3>
                    <Table>
                      <TableHeader>
                        <TableRow className="border-border">
                          <TableHead className="text-muted-foreground">#</TableHead>
                          <TableHead className="text-muted-foreground">Player</TableHead>
                          <TableHead className="text-center text-muted-foreground">P</TableHead>
                          <TableHead className="text-center text-muted-foreground">W</TableHead>
                          <TableHead className="text-center text-muted-foreground">L</TableHead>
                          <TableHead className="text-center text-muted-foreground">FW</TableHead>
                          <TableHead className="text-center text-muted-foreground">FL</TableHead>
                          <TableHead className="text-center text-muted-foreground font-bold">Pts</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {standings.map((s, i) => (
                          <TableRow key={s.player_id} className="border-border hover:bg-white/5">
                            <TableCell>{i + 1}</TableCell>
                            <TableCell className="font-medium">{s.player_name}</TableCell>
                            <TableCell className="text-center">{s.played || 0}</TableCell>
                            <TableCell className="text-center text-primary">{s.won || 0}</TableCell>
                            <TableCell className="text-center text-destructive">{s.lost || 0}</TableCell>
                            <TableCell className="text-center">{s.frames_won || 0}</TableCell>
                            <TableCell className="text-center">{s.frames_lost || 0}</TableCell>
                            <TableCell className="text-center font-bold text-secondary">{s.points || 0}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                ) : (
                  <p className="text-center text-muted-foreground py-8">No groups configured</p>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        )}

        {/* Bracket Tab */}
        <TabsContent value="bracket">
          <Card>
            <CardContent className="p-4">
              <BracketTabBody tournament={tournament} matches={matches} players={players} isAdmin={user?.is_admin} onGenerated={fetchData} onChanged={fetchData} />
            </CardContent>
          </Card>
        </TabsContent>

        {/* Players Tab */}
        <TabsContent value="players">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Annadil Burhani DSM Members ({registeredPlayers.length})</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
                {registeredPlayers.map(p => (
                  <Link key={p.id} to={`/players/${p.id}`}>
                    <div className="flex items-center gap-3 p-3 rounded-lg border border-border hover:border-primary/30 transition-colors">
                      <Avatar className="h-10 w-10">
                        <AvatarImage src={p.photo_url} />
                        <AvatarFallback className="text-sm">{p.name?.split(' ').map(n => n[0]).join('')}</AvatarFallback>
                      </Avatar>
                      <div>
                        <p className="font-medium text-sm">{p.name}</p>
                        <div className="flex gap-2 text-xs text-muted-foreground">
                          <span>Rank #{p.ranking}</span>
                          <span>ELO {p.elo_rating || 1500}</span>
                        </div>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Stats Tab */}
        <TabsContent value="stats">
          <div className="space-y-4">
            {stats && stats.total_matches > 0 ? (
              <>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <Card><CardContent className="p-4 text-center">
                    <p className="text-2xl font-heading font-bold text-primary">{stats.completed_matches}</p>
                    <p className="text-xs text-muted-foreground">Completed Matches</p>
                  </CardContent></Card>
                  <Card><CardContent className="p-4 text-center">
                    <p className="text-2xl font-heading font-bold text-secondary">{stats.highest_break}</p>
                    <p className="text-xs text-muted-foreground">Highest Break</p>
                  </CardContent></Card>
                  <Card><CardContent className="p-4 text-center">
                    <p className="text-2xl font-heading font-bold">{stats.total_frames}</p>
                    <p className="text-xs text-muted-foreground">Total Frames</p>
                  </CardContent></Card>
                  <Card><CardContent className="p-4 text-center">
                    <p className="text-2xl font-heading font-bold text-chart-3">{stats.total_centuries}</p>
                    <p className="text-xs text-muted-foreground">Centuries</p>
                  </CardContent></Card>
                </div>
                {stats.top_scorers?.length > 0 && (
                  <Card>
                    <CardHeader><CardTitle className="text-lg">Top Performers</CardTitle></CardHeader>
                    <CardContent>
                      <Table>
                        <TableHeader>
                          <TableRow className="border-border">
                            <TableHead className="text-muted-foreground">#</TableHead>
                            <TableHead className="text-muted-foreground">Player</TableHead>
                            <TableHead className="text-center text-muted-foreground">Frames Won</TableHead>
                            <TableHead className="text-center text-muted-foreground">Frames Lost</TableHead>
                            <TableHead className="text-center text-muted-foreground">High Break</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {stats.top_scorers.map((s, i) => (
                            <TableRow key={s.player_id} className="border-border hover:bg-white/5">
                              <TableCell>{i + 1}</TableCell>
                              <TableCell>
                                <Link to={`/players/${s.player_id}`} className="font-medium hover:text-primary">{s.name}</Link>
                              </TableCell>
                              <TableCell className="text-center text-primary font-medium">{s.frames_won}</TableCell>
                              <TableCell className="text-center">{s.frames_lost}</TableCell>
                              <TableCell className="text-center text-secondary">{s.highest_break}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </CardContent>
                  </Card>
                )}
              </>
            ) : (
              <Card>
                <CardContent className="p-8 text-center text-muted-foreground">
                  <ChartBar className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p>Stats will appear once matches are completed</p>
                </CardContent>
              </Card>
            )}

            {/* Highlights */}
            {highlights.length > 0 && (
              <Card data-testid="tournament-highlights">
                <CardHeader><CardTitle className="text-lg flex items-center gap-2"><Star className="w-5 h-5 text-secondary" weight="fill" />Match Highlights</CardTitle></CardHeader>
                <CardContent className="space-y-2">
                  {highlights.map(h => (
                    <div key={h.id} className="flex items-center gap-3 p-3 rounded-lg bg-secondary/5 border border-secondary/20">
                      <Lightning className="w-4 h-4 text-secondary shrink-0" weight="fill" />
                      <div className="flex-1">
                        <p className="text-sm font-medium">{h.description || h.type?.replace('_', ' ')}</p>
                        {h.player_name && <p className="text-xs text-muted-foreground">{h.player_name}{h.break_value ? ` - Break: ${h.break_value}` : ""}</p>}
                      </div>
                      {h.frame_number && <Badge variant="outline" className="text-[10px]">Frame {h.frame_number}</Badge>}
                    </div>
                  ))}
                </CardContent>
              </Card>
            )}
          </div>
        </TabsContent>
      </Tabs>

      <ProgressionDialog
        open={showProgression}
        onOpenChange={setShowProgression}
        tournamentId={tournament.id}
        onFinalized={fetchData}
      />

      <Dialog open={showGroupEditor} onOpenChange={setShowGroupEditor}>
        <DialogContent className="bg-card border-border max-w-2xl" data-testid="manage-groups-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UsersThree className="w-5 h-5 text-secondary" weight="fill" />
              Manage Groups moved
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 text-sm">
            <p>
              Group editing is now only available from the <strong>Admin → Tournaments</strong> tab.
              The destructive "wipe & recreate" was removed from this page after a serious data-loss
              incident on 29 Apr 2026.
            </p>
            <p className="text-muted-foreground">
              Open the Admin panel and pick this tournament to edit its groups.
            </p>
          </div>
        </DialogContent>
      </Dialog>

      <RescheduleMatchDialog
        open={!!reschedulingMatch}
        onOpenChange={(v) => { if (!v) setReschedulingMatch(null); }}
        match={reschedulingMatch}
        tournamentId={tournament.id}
        onChanged={fetchData}
      />

      <ShareMatchDialog
        open={!!sharingMatch}
        onOpenChange={(v) => { if (!v) setSharingMatch(null); }}
        match={sharingMatch}
      />
    </motion.div>
  );
}


/**
 * Renders the correct content for the Bracket tab.
 *
 * - If knockout matches exist → show the bracket.
 * - Else if this is a groups_knockout tournament whose group stage is still
 *   in progress → show a clear progress banner (how many matches remain) so
 *   the admin understands *why* no bracket is visible yet. This prevents the
 *   confusion reported on K Cup 2026 where group-stage matches were rendered
 *   on the Bracket tab and made it look "broken".
 * - Else (single-elimination with matches) → fall back to showing the matches.
 * - Else → empty state.
 */
function BracketTabBody({ tournament, matches, players, isAdmin, onGenerated, onChanged }) {
  const knockoutMatches = matches.filter(m => m.round && m.round !== "group_stage");
  if (knockoutMatches.length > 0) {
    return (
      <BracketView
        matches={knockoutMatches}
        players={players}
        canEdit={isAdmin}
        tournamentId={tournament.id}
        onChanged={onChanged}
      />
    );
  }

  const format = tournament?.tournament_format;
  if (format === "groups_knockout") {
    const groupMatches = matches.filter(m => m.round === "group_stage");
    const remaining = groupMatches.filter(
      m => m.status !== "completed" && m.status !== "walkover" && m.status !== "no_result"
    ).length;
    return (
      <div className="space-y-4" data-testid="bracket-tab-pending">
        <div className="text-center py-4 text-muted-foreground" data-testid="bracket-pending-banner">
          <TreeStructure className="w-10 h-10 mx-auto mb-2 opacity-50" />
          <p className="font-medium text-foreground mb-1">Knockout bracket not generated yet</p>
          {remaining > 0 ? (
            <p className="text-sm">
              Group stage is still in progress — <span className="text-primary font-semibold">{remaining}</span>{" "}
              match{remaining === 1 ? "" : "es"} remaining. Once all group matches are
              completed, the admin can click <span className="text-secondary">“Generate Knockout from Groups”</span> below.
            </p>
          ) : (
            <p className="text-sm">
              Group stage is complete. Click <span className="text-secondary">“Generate Knockout from Groups”</span> below to seed the bracket.
            </p>
          )}
        </div>
        <ProbableBracketCard
          tournamentId={tournament.id}
          isAdmin={isAdmin}
          onGenerated={onGenerated}
        />
      </div>
    );
  }

  if (matches.length > 0) {
    return (
      <BracketView
        matches={matches}
        players={players}
        canEdit={isAdmin}
        tournamentId={tournament.id}
        onChanged={onChanged}
      />
    );
  }
  return (
    <div className="text-center py-8 text-muted-foreground">
      <TreeStructure className="w-12 h-12 mx-auto mb-3 opacity-50" />
      <p>No bracket data available</p>
    </div>
  );
}
